import { createButtonComponent } from '@vfde-brix/button';
import { createHeadline, Headline, IHeadlineProperties } from '@vfde-brix/headline';
import { COLOR_WHITE, NO_PATTERN_BUSINESS_LOGIC, Price } from '@vfde-brix/core';
import { createJustText, IJustTextProperties, JustText } from '@vfde-brix/just-text';
import { createHighlightBadgeComponent, HighlightBadge, IHighlightBadgeProperties } from '@vfde-brix/highlight-badge';
import { createPromoPriceComponent, IPromoPriceProperties, PromoPrice } from '@vfde-brix/promo-price';
import {
    WRAPPER_ID_ADDITIONAL_LINKS,
    WRAPPER_ID_OFFER_PRICE_BUTTON,
    WRAPPER_ID_PROMO_PRICE,
    WRAPPER_ID_SELECTED_TARIFF_HEADLINE,
    WRAPPER_ID_TARIFF_LEGAL,
    WRAPPER_ID_TARIFF_STAIRWAY,
} from 'Constant';
import getPageOptions, { getAdditionalLink, getHighlightBadgeData, isFlexId } from 'Helper/additionalPageOptionsHelper';
import { SelectedOption } from '../container/App/interface';
import { SalesChannel } from 'app/enum';
import { HtmlId } from '../container/App/enum';
import { getEligiblePrice } from '../helper/priceHelper';
import { Tariff } from '../interface/dto/Tariffs';

const mountOfferPrice = (onOrderButtonClick: Function) => {
    const elHeadline = document.getElementById(WRAPPER_ID_SELECTED_TARIFF_HEADLINE);
    const headlineComponent = elHeadline && createHeadline(elHeadline, NO_PATTERN_BUSINESS_LOGIC);

    const elHighlightBadge = document.getElementById(HtmlId.HIGHLIGHT_BADGE);
    const highlightBadgeComponent = elHighlightBadge && createHighlightBadgeComponent(elHighlightBadge, NO_PATTERN_BUSINESS_LOGIC);

    const elPromoPrice = document.getElementById(WRAPPER_ID_PROMO_PRICE);
    const promoPriceComponent = elPromoPrice && createPromoPriceComponent(elPromoPrice, NO_PATTERN_BUSINESS_LOGIC);

    const elStairway = document.getElementById(WRAPPER_ID_TARIFF_STAIRWAY);
    const stairwayComponent = elStairway && createJustText(elStairway, NO_PATTERN_BUSINESS_LOGIC);

    const elLegal = document.getElementById(WRAPPER_ID_TARIFF_LEGAL);
    const legalComponent = elLegal && createJustText(elLegal, NO_PATTERN_BUSINESS_LOGIC);

    const elButton = document.getElementById(WRAPPER_ID_OFFER_PRICE_BUTTON);
    const buttonComponent = elButton && createButtonComponent(elButton, { onClick: onOrderButtonClick });

    return {
        headlineComponent,
        highlightBadgeComponent,
        promoPriceComponent,
        stairwayComponent,
        legalComponent,
        buttonComponent,
    };
};

type OfferComponent = ReturnType<typeof mountOfferPrice>;

const updateHeadline = (comp: Headline, selectedOption: SelectedOption) => {
    const props: Partial<IHeadlineProperties> = {
        stdContent: selectedOption.subscription?.tariffName || '',
    };

    comp.update(props);
};

const updateHighlightBadge = (comp: HighlightBadge, salesChannel: SalesChannel, selectedOption: SelectedOption) => {
    const isFlex = isFlexId(selectedOption.subscriptionId);
    const highlightBadgeData = getHighlightBadgeData(salesChannel, isFlex);
    const props: Partial<IHighlightBadgeProperties> = {
        hidden: !highlightBadgeData?.text,
        content: highlightBadgeData?.text,
        tooltip: highlightBadgeData?.tooltip ? {
            stdHeadline: highlightBadgeData.tooltip.headline,
            txtContent: highlightBadgeData.tooltip.text,
            optIconColor: COLOR_WHITE,
            business: NO_PATTERN_BUSINESS_LOGIC,
        } : undefined,
    };

    comp.update(props);
};

const updatePromoPriceComponent = (comp: PromoPrice, sub?: Tariff) => {
    if (!sub) {
        comp.hideContainer();
        return;
    }

    const price = Price.fromNumber(getEligiblePrice(sub) || 0);
    const props: Partial<IPromoPriceProperties> = {
        price,
    };

    comp.update(props);
    comp.showContainer();
};

export const updateDevicePriceText = (comp: JustText | null, selectedOption: SelectedOption, pattern = '{{hardwarePrice}}') => {
    const offerPriceData = getPageOptions()?.offerPrice;
    const { subscription } = selectedOption;
    const price = subscription?.prices?.composition?.hardware?.onetime?.withDiscounts?.gross !== undefined ?
        subscription?.prices?.composition?.hardware?.onetime?.withDiscounts?.gross :
        subscription?.prices?.composition?.hardware?.onetime?.withoutDiscounts?.gross !== undefined ?
            subscription?.prices?.composition?.hardware?.onetime?.withoutDiscounts?.gross :
            null;

    if (!price) {
        comp?.hideContainer();

        return;
    }

    const text = isFlexId(selectedOption.subscriptionId)
        ? offerPriceData?.hardwareFlexPriceTextPattern
        : offerPriceData?.hardwarePriceTextPattern;

    const convertedPrice = Number.isInteger(price)
        ? price.toString()
        : Price.fromNumber(price).toString();

    const props: Partial<IJustTextProperties> = {
        content: text?.replace(pattern, convertedPrice),
    };

    comp?.update(props);
    comp?.showContainer();
};

export const updateLegalText = (comp: JustText, selectedOption : SelectedOption, pattern = '{{connectionPrice}}') => {
    const text = getPageOptions()?.offerPrice.legalTextPattern;
    const { subscription } = selectedOption;
    const connectionPrice = subscription?.prices?.composition?.tariff?.onetime?.withDiscounts?.gross !== undefined ?
        subscription?.prices?.composition?.tariff?.onetime?.withDiscounts?.gross :
        subscription?.prices?.composition?.tariff?.onetime?.withoutDiscounts?.gross !== undefined ?
            subscription?.prices?.composition?.tariff?.onetime?.withoutDiscounts?.gross :
            null;

    if (!connectionPrice) {
        comp.hideContainer();
        return;
    }

    const props: Partial<IJustTextProperties> = {
        content: text?.replace(pattern, Price.fromNumber(connectionPrice).toString()),
    };

    comp.update(props);
    comp.showContainer();
};

export const updateOfferPrice = (comp: OfferComponent, selectedOtion: SelectedOption, salesChannel: SalesChannel) => {
    comp.headlineComponent && updateHeadline(comp.headlineComponent, selectedOtion);
    comp.highlightBadgeComponent && updateHighlightBadge(comp.highlightBadgeComponent, salesChannel, selectedOtion);
    comp.promoPriceComponent && updatePromoPriceComponent(comp.promoPriceComponent, selectedOtion.subscription);
    comp.legalComponent && updateLegalText(comp.legalComponent, selectedOtion);
};

/**
 * updateAdditionalLinks
 */
export const updateAdditionalLinks = (tariffId?: string) => {
    const containerEl = document.getElementById(WRAPPER_ID_ADDITIONAL_LINKS);
    const links = containerEl?.getElementsByTagName('a');

    if (!links || !tariffId) {
        return;
    }

    for (const link of Array.from(links)) {
        const href = getAdditionalLink(tariffId, link.id);

        if (href) {
            link.href = href;
        }
    }
};

export default mountOfferPrice;
